/**
 * Verification status of a module under one design code (CLAUDE.md 17, 18).
 *
 * "A module cannot be enabled in the registry unless its verification suite passed on
 * the current build. A red module is visible in the admin panel and hidden from
 * engineers."
 *
 * This badge is how that fact reaches a human. It matters more than it looks: the
 * entire value of the verified modules rests on §2 and §18, and an unverified module
 * presented with the same visual weight as a verified one is exactly the confusion
 * §1.3's firewall rule exists to prevent -- here in miniature, between two states of
 * the same module rather than between modules and sheets.
 *
 * The design-code name is never translated (14.4).
 */
import { CheckCircle2, CircleDashed, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/cn'

export type Verification = 'verified' | 'unverified' | 'failing'

const STYLES: Record<Verification, { icon: typeof CheckCircle2; className: string; key: string }> =
  {
    verified: {
      icon: CheckCircle2,
      className: 'text-[var(--status-pass)] bg-[var(--status-pass-bg)]',
      key: 'verification.verified',
    },
    unverified: {
      icon: CircleDashed,
      className: 'text-[var(--status-warn)] bg-[var(--status-warn-bg)]',
      key: 'verification.unverified',
    },
    failing: {
      icon: XCircle,
      className: 'text-[var(--status-fail)] bg-[var(--status-fail)]/10',
      key: 'verification.failing',
    },
  }

export function VerificationBadge({
  code,
  status,
  className,
}: {
  code: string
  status: Verification
  className?: string
}) {
  const { t } = useTranslation()
  const style = STYLES[status] ?? STYLES.unverified
  const Icon = style.icon

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs',
        style.className,
        className,
      )}
    >
      <Icon size={12} aria-hidden />
      {/* Code name untranslated; the status word translated. */}
      <span className="font-medium">{code}</span>
      <span className="sr-only sm:not-sr-only">{t(style.key)}</span>
    </span>
  )
}
